/*
 * The order of the people in a grid cell.
 *
 * A placement carries an optional `position`; older writes have none and sort on
 * their `createdAt` in milliseconds instead, which is the scale new positions
 * are chosen on, so both mix in one cell. Dropping someone between two others
 * gives them the midpoint of their neighbours' positions, so only the dropped
 * person is written.
 */

type Ordered = {
	createdAt: string;
	position?: number | null;
	user: { id: string };
};

/**
 * Where a placement sorts within its cell.
 * @param placement the placement
 * @returns its stored position, or its createdAt in milliseconds for older writes
 */
export const placementPosition = (placement: Pick<Ordered, 'createdAt' | 'position'>) =>
	placement.position ?? new Date(placement.createdAt).getTime();

/**
 * Sorts a cell's placements: by position, then by write moment, then by user id
 * so the order is stable when people share a position.
 * @param a a placement
 * @param b another placement
 * @returns the comparison result
 */
export const byCellOrder = <T extends Ordered>(a: T, b: T) =>
	placementPosition(a) - placementPosition(b) ||
	new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime() ||
	a.user.id.localeCompare(b.user.id);

/**
 * The placements in one cell, in the order the cell shows them.
 * @param placements all placements of the grid
 * @param row the cell row
 * @param column the cell column
 * @returns the cell's placements, sorted
 */
export const cellPlacements = <T extends Ordered & { row: number; column: number }>(
	placements: readonly T[],
	row: number,
	column: number,
) =>
	placements
		.filter((placement) => placement.row === row && placement.column === column)
		.sort(byCellOrder);

/**
 * Smallest gap between two neighbours that still fits a midpoint. Positions sit
 * on the millisecond scale (~1.8e12), where a double resolves about 2.4e-4.
 */
const MIN_GAP = 1e-3;

/** Distance from the outermost neighbour when someone is placed first or last. */
const STEP = 1000;

export type InsertPosition = {
	/** The position for the inserted person. */
	position: number;
	/**
	 * Set when the neighbours had no room left between them: new positions for
	 * everyone else in the cell too, spaced out again.
	 */
	renumbered?: Map<string, number>;
};

/**
 * The position for a person inserted at `index` among the others in a cell.
 * @param others the cell's placements without the inserted person, in cell order
 * @param index where the person goes among them; `others.length` appends
 * @returns the position, plus renumbered positions for the others when needed
 */
export const insertPosition = <T extends Ordered>(
	others: readonly T[],
	index: number,
): InsertPosition => {
	const before = others[index - 1];
	const after = others[index];

	if (!before && !after) {
		return { position: Date.now() };
	}
	if (!before) {
		return { position: placementPosition(after) - STEP };
	}
	if (!after) {
		return { position: placementPosition(before) + STEP };
	}

	const low = placementPosition(before);
	const high = placementPosition(after);
	if (high - low >= MIN_GAP) {
		return { position: low + (high - low) / 2 };
	}

	// No room left between the two: spread the whole cell out again from the
	// first person's position, keeping their order.
	const base = placementPosition(others[0]);
	const renumbered = new Map<string, number>();
	others.forEach((other, i) => {
		// The inserted person takes slot `index`; the others after it shift one slot.
		renumbered.set(other.user.id, base + (i >= index ? i + 1 : i) * STEP);
	});
	return { position: base + index * STEP, renumbered };
};
